import React, { useState, useReducer, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import I18n from 'utils/I18n';
import { Markup } from 'interweave';
import Spinner from '@strava/ui/Spinner';
import {
  InfiniteLoader,
  CellMeasurer,
  List,
  AutoSizer,
  CellMeasurerCache,
  WindowScroller
} from 'react-virtualized';
import { minBy } from 'lodash-es';
import styles from './feed.scss';

import Activity from './components/Activity';
import Post from './components/Post';
import FancyPromo from './components/FancyPromo';
import PerformanceGoalCreated from './components/PerformanceGoalCreated';
import ChallengeJoin from './components/ChallengeJoin';
import SimplePromo from './components/SimplePromo';
import GroupActivity from './components/GroupActivity';
import ClubJoin from './components/ClubJoin';

import { reducer } from './state/feed-reducer';

import { feedTypeProps, pageProps } from './props/feed-prop-types';
import useFetchFeedEntries from './hooks/useFetchFeedEntries';
import FeedContext from './state/FeedContext';
import { devLog } from './utils';
import { REGISTER_FEED_ENTRY_UI_CHANGE } from './state/feed-actions';

// Cursor pagination is key for the feed so this function has been deliberately
// exported so that it can be unit tested separately.
export const computeMinCursorData = (entries) => {
  return {
    minBeforeEntry: minBy(entries, (e) => {
      return e.cursorData.updated_at;
    }),
    minRankEntry: minBy(entries, (e) => {
      return e.cursorData.rank;
    })
  };
};

const Feed = ({
  currentAthleteId,
  page,
  feedType,
  athleteProfileId,
  clubId,
  useMockData,
  preFetchedEntries,
  locale
}) => {
  const {
    isLoading,
    error,
    entries,
    pagination,
    loadEntries
  } = useFetchFeedEntries({
    currentAthleteId,
    clubId,
    feedType,
    page,
    useMockData,
    preFetchedEntries
  });

  const [entityState, dispatch] = useReducer(reducer, { count: 0 });

  const providerState = {
    entityState,
    dispatch
  };

  // cache stores the row heights needed for virtualized scrolling
  const [cache] = useState(
    new CellMeasurerCache({ defaultHeight: 10, fixedWidth: true })
  );

  // listRef is needed to update the sizes of the feed cards
  // in the feed list if they change
  const [listRef, setListRef] = useState(null);

  // AutoSizer#onResize gets calls even on the initial render
  // This keeps track of the initial render so that the list is not force updated the first time
  const [autoSizerInitialRender, setAutoSizerInitialRender] = useState(true);

  const initialRender = useRef(true);

  // If a specific feed entry has changed size then you need to clear its
  // cached size so it can be remeasured. (eg. adding a comment on an activity)
  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
    } else if (!autoSizerInitialRender) {
      devLog('[feed] updating grid');
      cache.clearAll();
      listRef.forceUpdateGrid();
    }
    // only trigger when count changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityState.count]);

  const { maxEntries, hasMore } = pagination;
  const i18nPrefix = 'components.feed';

  // return the proper feed card that can be rendered
  const renderEntry = (params) => {
    const { entryProps, key, index } = params;
    const props = Object.assign(entryProps, {
      athleteProfileId, // for analytics - when viewing feed on athlete page
      clubId, // for analytics - when viewing feed on club details page
      index,
      page,
      feedType,
      key,
      locale
    });
    switch (entryProps.entity) {
      case 'Activity':
        return <Activity {...props} />;
      case 'Post':
        return <Post {...props} />;
      case 'FancyPromo':
        return <FancyPromo {...props} />;
      case 'PerformanceGoalCreated':
        return <PerformanceGoalCreated {...props} />;
      case 'Challenge':
        return <ChallengeJoin {...props} />;
      case 'SimplePromo':
        return <SimplePromo {...props} />;
      case 'GroupActivity':
        return <GroupActivity {...props} />;
      case 'Club':
        return <ClubJoin {...props} />;
      default:
        devLog(`Could not translate props into entry - ${entryProps.entity}`);
        return <div>N/A RF</div>; // TODO: Replace this with sentry error logging in monorepo
    }
  };

  const endMessage = () => {
    if (page === 'dashboard' && !isLoading) {
      const content = maxEntries
        ? `${i18nPrefix}.no_more.limit`
        : `${i18nPrefix}.no_more.days`;
      return (
        <div className={`${styles.feedPagination} ${styles.noEntries}`}>
          {I18n.t(content)}
          <br />
          <Markup
            noWrap={true}
            content={I18n.t(`${i18nPrefix}.no_more.full_html`, {
              athlete_id: currentAthleteId
            })}
          />
        </div>
      );
    }
    return null;
  };

  const loader = () => {
    return (
      <div className={styles.feedPagination}>
        <div className={styles.spinner}>
          {error ? I18n.t(`${i18nPrefix}.error_message`) : <Spinner />}
        </div>
      </div>
    );
  };

  // Invoked when more rows must be loaded.
  // Note: This function may be called multiple times in response to a single scroll event.
  // See README for details on how pagination works on the feed
  const loadMoreRows = ({ startIndex, stopIndex }) => {
    devLog(`[Feed#loadMoreRows] start: ${startIndex} stop: ${stopIndex}`);
    let minBeforeEntry;
    let minRankEntry;
    if (entries.length > 0) {
      ({ minBeforeEntry, minRankEntry } = computeMinCursorData(entries));
    }

    return new Promise((resolve) => {
      loadEntries({
        cursorData: {
          before: minBeforeEntry ? minBeforeEntry.cursorData.updated_at : null,
          rank: minRankEntry ? minRankEntry.cursorData.rank : null
        }
      }).then(() => {
        resolve();
      });
    });
  };

  // This is a temporary fix to insert padding at the bottom of each card entry
  // TODO [fumba] - implement this in scss
  const cardEntryStyles = { paddingBottom: '10px' };

  // Number of card entries on the feed
  // If there are more items to be loaded, we add an extra row to hold a
  // loading indicator / spinner.
  const rowCount = (() => (hasMore ? entries.length + 1 : entries.length))();

  // responsible for tracking the loaded state of each row.
  const isRowLoaded = ({ index }) => {
    return !hasMore || index < entries.length;
  };

  // responsible for rendering a single card entry (row), given its index
  const rowRenderer = (params) => {
    // [ named parameters from react-virtualized ]
    // index - index of row
    // key - unique key within array of rows
    // style - style object to be applied to row
    // parent - reference to the parents list
    const { index, key, style, parent } = params;

    if (!isRowLoaded({ index })) {
      return null; // feed card has already been rendered
    }
    return (
      <div key={key} style={style}>
        {/* automatically measures a cells content by temporarily rendering it
        in a way that is not visible to the user */}
        <CellMeasurer
          cache={cache}
          columnIndex={0}
          key={key}
          parent={parent}
          rowIndex={index}
        >
          {({ measure }) => (
            <div key={index} onLoad={measure} style={cardEntryStyles}>
              {renderEntry({ entryProps: entries[index], key, index })}
            </div>
          )}
        </CellMeasurer>
      </div>
    );
  };

  return (
    <FeedContext.Provider value={providerState}>
      <div className={styles.virtualizedFeed}>
        <InfiniteLoader
          isRowLoaded={isRowLoaded}
          loadMoreRows={loadMoreRows}
          rowCount={rowCount}
        >
          {({ onRowsRendered, registerChild }) => (
            <WindowScroller>
              {({ height, isScrolling, onChildScroll, scrollTop }) => (
                <AutoSizer
                  disableHeight={true}
                  onResize={() => {
                    if (autoSizerInitialRender) {
                      setAutoSizerInitialRender(false);
                    } else {
                      // force update components if onResize is called after autosizer was already initialized
                      dispatch({
                        type: REGISTER_FEED_ENTRY_UI_CHANGE
                      });
                    }
                  }}
                >
                  {({ width }) => (
                    <List
                      autoHeight={true}
                      deferredMeasurementCache={cache}
                      height={height}
                      onRowsRendered={onRowsRendered}
                      ref={(ref) => {
                        registerChild(ref);
                        setListRef(ref);
                      }}
                      rowCount={rowCount}
                      rowHeight={cache.rowHeight}
                      rowRenderer={rowRenderer}
                      width={width}
                      isScrolling={isScrolling}
                      onScroll={onChildScroll}
                      scrollTop={scrollTop}
                    />
                  )}
                </AutoSizer>
              )}
            </WindowScroller>
          )}
        </InfiniteLoader>
        {isLoading && loader()}
        {!hasMore && endMessage()}
      </div>
    </FeedContext.Provider>
  );
};

Feed.propTypes = {
  currentAthleteId: PropTypes.number.isRequired,
  page: pageProps.isRequired,
  feedType: feedTypeProps.isRequired,
  athleteProfileId: PropTypes.string.isRequired,
  clubId: PropTypes.string.isRequired,
  useMockData: PropTypes.bool.isRequired,
  preFetchedEntries: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  locale: PropTypes.string.isRequired
};
export default Feed;
